import {Injectable} from "@angular/core";
import {
  ApplicationLogDetailsRequest,
  ApplicationLogsRequest,
  ApplicationLogStepRequest,
  ApplicationLogsUpdateRequest,
  Log
} from "leia-api";
import {UtilitiesService} from "./utilities.service";
import {FacadeService} from "../service-facade/facade.service";
import {OnboardingApplicant} from "../model/onboarding-applicant.model";
import {ApplicantType} from "../../signup/models/applicant-type";
import {ReferenceCode} from "../../process/models/referenceCode";
import {CustomerControllerService, EligibilityResponse} from "skyhopper-api";
import {OnboardingRequest} from "../model/onboarding-request.model";
import {Subject, BehaviorSubject, Observable} from "rxjs";
import StatusEnum = ApplicationLogsUpdateRequest.StatusEnum;
import OnboardingSourceEnum = ApplicationLogStepRequest.OnboardingSourceEnum;
import {EMPLOYEE_SAVINGS_PRODUCT_UID, KYC_FORCE_KEY} from "../constants";
import {ProcessService} from "../../process/process.service";

interface EligibilityCriteriaResult {
  eligible: boolean;
  log: Log;
}

interface EligibilityResult {
  errorCode: ReferenceCode | undefined,
  isEligible: boolean
}

@Injectable({
  providedIn: 'root'
})
export class EligibilityService {

  constructor(private utils: UtilitiesService,
              private facadeService: FacadeService,
              private customerService: CustomerControllerService,
              private processService: ProcessService) {
  }

  private _customeEligibilityCheck = new BehaviorSubject<EligibilityResponse | null>(null);
  customerEligibility$ = this._customeEligibilityCheck.asObservable();

  customerEligibilityError = new BehaviorSubject<any | undefined>(undefined);

  private _canProceed = new BehaviorSubject<boolean>(false); // Initialize with a default value of false
 
  canProceed$ = this._canProceed.asObservable(); 

  getCustomerEligibility(sessionId: string): Promise<EligibilityResponse> {
    return this.customerService.getCustomerEligibility(sessionId).toPromise();
  }

  async checkCustomerEligibility(): Promise<EligibilityResponse | null> {
    try {
      const customerEligibility = await this.customerService.getCustomerEligibility(this.facadeService.getSessionId()).toPromise();

      if (customerEligibility.rcCode) {
        this.customerEligibilityError.next(customerEligibility.rcCode);
        return customerEligibility;
      }

      this._customeEligibilityCheck.next(customerEligibility);

      return customerEligibility;

    } catch (error) {
      this.customerEligibilityError.next(error);
      return null;
    }
  }

  async updateApplicationEligibilityFields() {
    let onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    let applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();

    let logs: Log[] = [];
    let applicationLogDetailRequests : ApplicationLogDetailsRequest[] = [];

    logs.push(this.utils.createLog("BB8", "Checking enrollment state for all applicants."));
    applicationLogDetailRequests.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "Checking enrollment state for all applicants."));

    // check applicant(s) eligibility
    const eligibilityResponse = this._customeEligibilityCheck.value!;

    logs.push(this.utils.createLog("BB8", "Eligibility Response: " + JSON.stringify(eligibilityResponse)));
    applicationLogDetailRequests.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "Eligibility Response: " + JSON.stringify(eligibilityResponse)));
    this.mapFieldsToApplicants(onboardingRequest, applicationRequest, eligibilityResponse, applicationLogDetailRequests);

    let step = this.utils.createEmptyStepForLogging(logs);

    applicationRequest.step = step;
    applicationRequest.applicationLogDetailsRequestList = applicationLogDetailRequests;

    this.facadeService.setShoppingCartApplicationLogsRequest(applicationRequest);
    this.facadeService.setShoppingCartOnboardingRequest(onboardingRequest);
    await this.facadeService.updateApplication();
  }

  private _createRejectionStep(errorCode: ReferenceCode, applicationRequest: ApplicationLogsRequest, logs: Log[], applicationLogDetailRequests: ApplicationLogDetailsRequest[]): ({ errorCode: ReferenceCode, isEligible: boolean }) {
    applicationRequest.step = this.utils.createEmptyStepForLogging(logs);
    applicationRequest.applicationLogDetailsRequestList = applicationLogDetailRequests;
    applicationRequest.status = errorCode === ReferenceCode.EligibilityReviewFailure ? StatusEnum.Error : StatusEnum.Rejected;
    applicationRequest.rcCode = errorCode.toString();

    return {errorCode, isEligible: false};
  }

  mapFieldsToApplicants(onboardingRequest: OnboardingRequest, applicationRequest: ApplicationLogsRequest, response: EligibilityResponse, applicationLogDetailRequests : ApplicationLogDetailsRequest[]) {
    onboardingRequest.applicants.forEach(applicant => {
      let eligibilityResponse = response.applicants?.find(appEligibility => appEligibility.applicantId === applicant.applicantId);

      if (!eligibilityResponse) return;

      const {isInGoodStanding, isExisting, isEnrolledInOLB, employeeType, hasInfoMismatch} = eligibilityResponse!;

      let isKycRequired = eligibilityResponse!.isKycRequired!;
      let isAlloyEvaluateRequired = eligibilityResponse!.isAlloyEvaluateRequired;
      let alloyEntityTokenId = eligibilityResponse!.alloyEntityTokenId;

      this.processService.alloyExistingCustomerMap.set(applicant.applicantId!, eligibilityResponse.isExistingCustomerFlagForAlloy!);

      let isKycForce = this.facadeService.getStorageItem(KYC_FORCE_KEY);

      if (isKycForce) {
        isKycRequired = true;
        isAlloyEvaluateRequired = true;
        alloyEntityTokenId = "";

        applicationLogDetailRequests.push(this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "KYC Forced via Settings."));
      }

      if (applicant.isMinor) isKycRequired = false;

      applicant.customerInfo.isInGoodStanding = isInGoodStanding;
      applicant.customerInfo.requiresKyc = isKycRequired;
      applicant.customerInfo.isAlloyEvaluateRequired = isAlloyEvaluateRequired;
      applicant.customerInfo.alloyEntityTokenId = alloyEntityTokenId;
      applicant.customerInfo.isEnrolledInOLB = isEnrolledInOLB;
      applicant.customerInfo.isExisting = isExisting;
      applicant.customerInfo.employeeType = employeeType;
      applicant.customerInfo.hasInfoMismatch = hasInfoMismatch;

      let reqApplicant = applicationRequest.applicants?.find(reqApplicant => reqApplicant.applicantId === applicant.applicantId);

      if (reqApplicant) {
        reqApplicant.isKyc = isKycRequired;
        reqApplicant.isInGoodStanding = isInGoodStanding;
        reqApplicant.isEnrolledOLB = isEnrolledInOLB;
        reqApplicant.isExistingCustomer = isExisting;
        reqApplicant.employeeType = employeeType;
      }

      //OLB Credentials Check
      //set only based on primary applicant olb enrollment flag.
      if (applicant.applicantType === ApplicantType.Primary) {
        this.facadeService.reportOLBEnrollment(!isEnrolledInOLB);
      }
    });
  }

 setCanProceed(value: boolean): void {
  this._canProceed.next(value); 
}

// Get the current value of canProceed
getCanProceed(): boolean {
  return this._canProceed.getValue();
}

// Expose the Observable for subscriptions
getCanProceedObservable(): Observable<boolean> {
  return this.canProceed$; 
}
  
}

export enum EligibilityStatus {
  Success = "Success",
  Failure = "Failure"
}
